// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Accounting API client (Stage 6P.6).
//
// Thin axios wrappers around:
//   /api/erp/purchase-accounting/*  (Purchase Bill Accounting Posting
//                                    Foundation — 6P.6)
//
// Mirrors the inventoryApi shape — the `unwrap` helper handles both
// the {data:{data:...}} envelope used by most list/create endpoints and
// the bare {data:...} shape used by helper / status / preview routes.
//
// Backend is the source of truth for ALL accounting math:
//   ledger code resolution (per item-type with PURCHASE fallback),
//   debit/credit balance enforcement, round-off classification,
//   GST ITC eligibility, supplier-payable open-balance computation,
//   duplicate-block decisions.
// Frontend mirrors for instant UX but never wins.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const PA_BASE = '/api/erp/purchase-accounting';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const purchaseAccountingApi = {
  // ── Posting reads ─────────────────────────────────────────────
  listPostings:        (params = {}) =>
    client.get(`${PA_BASE}/postings`, { params }).then(unwrap),
  getPosting:          (id) =>
    client.get(`${PA_BASE}/postings/${encodeURIComponent(id)}`).then(unwrap),
  getPostingStatus:    (billId) =>
    client
      .get(`${PA_BASE}/status/${encodeURIComponent(billId)}`)
      .then(unwrap),

  // ── Preview / post / cancel ──────────────────────────────────
  // Preview is POST (not GET) per the backend route spec.
  previewPosting:      (billId) =>
    client
      .post(`${PA_BASE}/preview/${encodeURIComponent(billId)}`, {})
      .then(unwrap),
  postPosting:         (billId, payload = {}) =>
    client
      .post(`${PA_BASE}/post/${encodeURIComponent(billId)}`, payload)
      .then(unwrap),
  cancelPosting:       (id, reason) =>
    client
      .post(`${PA_BASE}/postings/${encodeURIComponent(id)}/cancel`, { reason })
      .then(unwrap),

  // ── GST ITC register ──────────────────────────────────────────
  listGstItc:          (params = {}) =>
    client.get(`${PA_BASE}/gst-itc`, { params }).then(unwrap),
  getGstItcByBill:     (billId) =>
    client
      .get(`${PA_BASE}/gst-itc/bill/${encodeURIComponent(billId)}`)
      .then(unwrap),

  // ── Unposted bills & supplier payables ────────────────────────
  listUnpostedBills:   (params = {}) =>
    client.get(`${PA_BASE}/unposted-bills`, { params }).then(unwrap),
  listSupplierPayables: (params = {}) =>
    client.get(`${PA_BASE}/supplier-payables`, { params }).then(unwrap)
};

export default { purchaseAccountingApi };
